import { Alert, Button, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { AuthLayout } from "@/components/AuthLayout";
import { useI18n } from "@/i18n";

/**
 * Opened from Console Zerpid (Plan §6.16): the ticket in the URL fragment
 * (never sent to a server log) becomes a read-only support session.
 */
export function SupportSession() {
  const { t } = useI18n();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const errorText = useErrorText();
  // Read once; the fragment is cleared from the address bar right away.
  const [ticket] = useState(() => window.location.hash.slice(1));
  const [error, setError] = useState<unknown>(ticket ? null : new Error("no ticket"));
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !ticket) return;
    started.current = true;
    window.history.replaceState(null, "", window.location.pathname);
    unwrap(api.POST("/v1/auth/support", { body: { ticket } }))
      .then(async (session) => {
        await signIn(session);
        navigate("/app", { replace: true });
      })
      .catch(setError);
  }, [navigate, signIn, ticket]);

  return (
    <AuthLayout title={t("support.title")} subtitle={t("support.subtitle")}>
      {error ? (
        <Alert
          type="error"
          showIcon
          title={errorText(error)}
          action={
            <Button size="small" onClick={() => navigate("/login")}>
              {t("support.toLogin")}
            </Button>
          }
        />
      ) : (
        <Spin />
      )}
    </AuthLayout>
  );
}
